import { Box, Button, Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { EASY_EDU_SERVER_BASE_URL } from "../../environment";
import { APIS } from "../common/api/apis";
import { GradeSectionComponent } from "../common/shared/GradeSectionComponent";
import { PROMOTE_STUDENT_VIEW } from "../common/shared/messages";
import { ACADEMIC_STATUS, AcademicStatus } from "../common/shared/types";
import { useStudents } from "../../features/student/hooks/useStudents";
import { Student } from "../../features/student/types";

const FINAL_GRADE_NAME = "ten";

const getAcademicStatusOptions = (gradeName: string | undefined): AcademicStatus[] => {
  if (gradeName?.trim().toLowerCase() === FINAL_GRADE_NAME) {
    return [ACADEMIC_STATUS.Repeated, ACADEMIC_STATUS.Graduated];
  }
  return [ACADEMIC_STATUS.Promoted, ACADEMIC_STATUS.Dropped, ACADEMIC_STATUS.Transferred];
};

const getDefaultAcademicStatus = (gradeName: string | undefined): AcademicStatus =>
  gradeName?.trim().toLowerCase() === FINAL_GRADE_NAME ? ACADEMIC_STATUS.Graduated : ACADEMIC_STATUS.Promoted;

export const Promote = () => {
  const [dataLoaded, setDataLoaded] = useState<boolean>();
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [academicStatusByAdmissionNo, setAcademicStatusByAdmissionNo] = useState<Record<string, AcademicStatus>>({});
  const [isPromoting, setIsPromoting] = useState<boolean>(false);
  const { students, isLoading, error, actions } = useStudents(grade, section, "ACTIVE");

  useEffect(() => {
    setDataLoaded(!isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!students) return;
    setAcademicStatusByAdmissionNo(
      students.reduce((acc, student) => {
        acc[student.admissionNo] = getDefaultAcademicStatus(student.gradeName);
        return acc;
      }, {} as Record<string, AcademicStatus>)
    );
  }, [students]);

  const changeAcademicStatus = (e: SelectChangeEvent<AcademicStatus>, admissionNo: string) => {
    setAcademicStatusByAdmissionNo((prev) => ({ ...prev, [admissionNo]: e.target.value as AcademicStatus }));
  };

  const submitPromoteStudents = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsPromoting(true);

    const feedueConfig = {
      headers: {
        "Content-Type": "application/json",
      },
    };

    try {
      let url = APIS.PROMOTE_STUDENTS;
      await axios
        .post(EASY_EDU_SERVER_BASE_URL + url, academicStatusByAdmissionNo, feedueConfig)
        .then(() => {
          alert("Promoted Successfully!!!");
          setDataLoaded(false);
        })
        .catch((error) => {
          setIsPromoting(false);
          alert(error.response.data);
          console.log(error);
        });
    } catch (e) {
      setIsPromoting(false);
      console.log(e);
    }
  };

  const fewStudentsHasPreviousDue = students?.some((std) => (std.totalDue ?? 0) > 0);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyItems: "center" }}>
        <Grid>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
              justifyItems: "center",
              marginLeft: "5px",
              marginBottom: "10px",
              marginTop: "10px",
            }}
            onSubmit={(e) => {
              e.preventDefault();
              actions.reload();
            }}
          >
            <GradeSectionComponent
              grade={grade}
              setGrade={(grade) => {
                setGrade(grade);
              }}
              section={section}
              setSection={(section) => setSection(section)}
              statusListRequired={false}
              message={PROMOTE_STUDENT_VIEW.GRADE_SECTION_MESSAGE}
            />

            <Button type="submit" variant="contained" color="primary" size="small">
              Get
            </Button>
          </Box>
        </Grid>
        <Grid>{dataLoaded && !error ? <Grid>{"Total Students : " + (students?.length ?? 0)}</Grid> : ""}</Grid>
      </Box>

      {error ? <h3 className="Roboto-Heading-RED-FONT">{error}</h3> : ""}

      <div>
        {dataLoaded && !error && students?.length > 0 ? (
          <Box
            component="form"
            onSubmit={submitPromoteStudents}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "flex-start",
              marginLeft: "5px",
            }}
          >
            <TableContainer className="mui-table-container-style" >
              <Table
                size="small"
                className="mui-table-bordered"
                sx={{
                  tableLayout: "fixed",
                  "& .MuiTableCell-root": { py: 0.5, px: 1, fontSize: 13 },
                  "& .MuiTableCell-head": { fontWeight: 600 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Admission No</TableCell>
                    {fewStudentsHasPreviousDue ? <TableCell sx={{ width: 90 }}>Total Due</TableCell> : ""}
                    <TableCell sx={{ width: 150 }}>Guardian Phone No</TableCell>
                    <TableCell sx={{ width: 180 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students?.map((pstd: Student) => (
                    <TableRow key={pstd.admissionNo}>
                      <TableCell>{pstd.name}</TableCell>
                      <TableCell>{pstd.admissionNo}</TableCell>
                      {fewStudentsHasPreviousDue ? <TableCell>{pstd.totalDue}</TableCell> : ""}
                      <TableCell>{pstd.guardian.mobileNo}</TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={academicStatusByAdmissionNo[pstd.admissionNo] ?? getDefaultAcademicStatus(pstd.gradeName)}
                          onChange={(e) => changeAcademicStatus(e, pstd.admissionNo)}
                          sx={{ width: 140, fontSize: 13, "& .MuiSelect-select": { py: 0.5 } }}
                        >
                          {getAcademicStatusOptions(pstd.gradeName).map((status) => (
                            <MenuItem value={status} key={status} sx={{ fontSize: 13 }}>
                              {status}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button type="submit" variant="contained" color="primary" size="small" loading={isPromoting} loadingIndicator="Promoting…" sx={{ mt: 1, mb: 1 }}>
              Promote
            </Button>
          </Box>
        ) : (
          ""
        )}
        <div>{dataLoaded && !error && students?.length === 0 ? <h3 className="Roboto-Heading-RED-FONT">No students found for the selected criteria.</h3> : ""}</div>
      </div>
    </Box>
  );
};
